import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import calendrierWhiteIcon from "@/assets/icons/calendrier-white.webp";
import mascotte from "@/assets/mascotte-autosur.webp";

interface CTASectionProps {
  title?: string;
  subtitle?: string;
}

export const CTASection = ({ 
  title = "Besoin d'un contrôle technique ?",
  subtitle = "Prenez rendez-vous dès maintenant et bénéficiez d'un service rapide et professionnel."
}: CTASectionProps) => {
  return (
    <section className="py-16 bg-gradient-to-br from-autosur-navy to-autosur-blue relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzMiAyIDIgNC0yIDQtMiA0LTItMi0yLTR6Ii8+PC9nPjwvZz48L3N2Zz4=')]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex items-center justify-center gap-8 lg:gap-16">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl"
          >
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
              {title}
            </h2>
            <p className="font-body text-white/70 text-lg mb-8">
              {subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-colors shadow-lg"
              >
                <img src={calendrierWhiteIcon} alt="" className="w-6 h-6" width={24} height={24} />
                Prendre rendez-vous
              </Link>
              <a
                href="tel:+33149889240"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-autosur-navy font-heading font-semibold rounded-full hover:bg-white/90 transition-colors shadow-lg"
              >
                <Phone className="w-5 h-5" />
                01 49 88 92 40
              </a>
            </div>

            {/* Mascotte - visible uniquement sur mobile */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:hidden flex justify-center mt-8"
            >
              <motion.img
                src={mascotte}
                alt="Mascotte Autosur"
                className="w-28 h-28 object-contain drop-shadow-lg"
                loading="lazy"
                width={96}
                height={96}
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </motion.div>

          {/* Mascotte - visible uniquement sur desktop */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block flex-shrink-0 mr-[-3.5rem] xl:mr-[-5rem]"
          >
            <motion.img
              src={mascotte}
              alt="Mascotte Autosur"
              className="w-36 h-36 xl:w-44 xl:h-44 object-contain drop-shadow-lg"
              loading="lazy"
              width={144}
              height={144}
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
